import React from 'react';

/**
 * WaitingRow — one "waiting on you" item (Epic 4 · S4.2).
 *
 * Modelled on the weather-intelligence `ActionList.js` pattern: a ranked text
 * row with actions. One row per item, in the order given (the panel never
 * re-sorts — §5.7).
 *
 * Row layout (Part E.2): title (`.gi-body font-medium`) · park (multi-park
 * view only) · `asked_label` · `when` chip · **Reply** · **Open**.
 *   - Reply  → opens ItemDetail with the composer focused (`focusComposer:true`).
 *   - Open   → opens ItemDetail (`focusComposer:false`).
 *   - Both actions are ALWAYS visible and ≥44px (Part C touch target). Colour is
 *     never the only carrier — every action has its text label.
 *
 * `when` chip (acceptance, Epic 4): `null` → no chip; `{kind:'promised'}` →
 * neutral chip; `{kind:'done'}` → emerald chip. A plain string (a date) renders
 * as a neutral chip. No red/amber anywhere on this row — the only urgency on
 * the page is the panel-level act-on-this accent, not a row colour.
 *
 * The row is presentational; it calls `onOpenItem(item, { focusComposer })` and
 * the shell (`PortalPage`) pushes the item drill frame onto the one `DrillOver`.
 */
const NEUTRAL_CHIP = 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
const EMERALD_CHIP = 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';

// `when` may be null, a string (a date), or an object `{ kind, label }`. Only
// `{kind:'done'}` is emerald; everything else (promised, a date, an unknown
// kind) is the neutral chip. An empty label renders no chip (never a placeholder).
const WhenChip = ({ when }) => {
  if (when == null) return null;
  let label;
  let emerald = false;
  if (typeof when === 'object') {
    emerald = when.kind === 'done';
    label = when.label || when.kind || '';
  } else {
    label = String(when);
  }
  if (!label) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${emerald ? EMERALD_CHIP : NEUTRAL_CHIP}`}
      data-when-kind={typeof when === 'object' ? when.kind : undefined}
    >
      {label}
    </span>
  );
};

// ≥44px touch target for every action (Part C). Primary = the act-on-this blue
// used only on this panel; Open is the quiet outline action so both read as
// distinct, equally-reachable controls.
const ACTION_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg min-h-[44px] px-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]';

const WaitingRow = ({ item, multiPark = false, onOpenItem }) => {
  const handleReply = () => onOpenItem?.(item, { focusComposer: true });
  const handleOpen = () => onOpenItem?.(item, { focusComposer: false });

  return (
    <li className="flex items-center gap-3 flex-wrap py-2" data-testid="waiting-row" data-item-id={item?.id}>
      {/* Title + meta — the row's text content. */}
      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
        <span className="gi-body font-medium text-gray-900 dark:text-gray-100 break-words">
          {item?.title}
        </span>
        {multiPark && item?.park && (
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.park}</span>
        )}
        {item?.asked_label && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{item.asked_label}</span>
        )}
        <WhenChip when={item?.when} />
      </div>

      {/* Actions — always visible, ≥44px (Part C). */}
      <button
        type="button"
        onClick={handleReply}
        className={`${ACTION_BASE} text-white bg-[#32B1FF] hover:bg-[#1aa3f5]`}
        aria-label={`Reply to ${item?.title || 'item'}`}
      >
        Reply
      </button>
      <button
        type="button"
        onClick={handleOpen}
        className={`${ACTION_BASE} text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800`}
        aria-label={`Open ${item?.title || 'item'}`}
      >
        Open
      </button>
    </li>
  );
};

export default WaitingRow;
